import {
  ListTodo,
  RefreshCw,
  XCircle,
  CheckCircle,
  ChevronDown,
  AlertCircle,
  ExternalLink,
} from 'lucide-react'
import clsx from 'clsx'
import type { ActionItem } from '../external-types'

export interface MyActionsDrawerProps {
  render: boolean
  visible: boolean
  onClose: () => void

  actionItems: ActionItem[]
  loadingActions: boolean
  onRefreshActions: () => void

  actionFilter: 'all' | 'jira' | 'github' | 'critical'
  onSetActionFilter: (filter: 'all' | 'jira' | 'github' | 'critical') => void

  showCompletedActions: boolean
  onToggleCompletedActions: () => void

  onExploreAction: (item: ActionItem) => void
}

// Human-friendly relative timestamp
function formatRelativeTime(input: string | Date | undefined | null) {
  if (!input) return ''
  const then = input instanceof Date ? input.getTime() : new Date(input).getTime()
  if (Number.isNaN(then)) return ''
  const diffSec = Math.max(0, (Date.now() - then) / 1000)
  if (diffSec < 45) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`
  if (diffSec < 86400 * 28) return `${Math.floor(diffSec / (86400 * 7))}w ago`
  return new Date(then).toLocaleDateString()
}

function priorityStripeClass(priority: string | undefined) {
  switch (priority) {
    case 'critical':
      return 'bg-destructive'
    case 'high':
      return 'bg-warning'
    case 'medium':
      return 'bg-warning'
    case 'low':
      return 'bg-info/60'
    default:
      return 'bg-foreground/20'
  }
}

const sourceOrder = ['jira', 'github', 'other'] as const
const sourceLabel: Record<string, string> = {
  jira: 'Jira',
  github: 'GitHub',
  other: 'Other',
}

export function MyActionsDrawer({
  render,
  visible,
  onClose,
  actionItems,
  loadingActions,
  onRefreshActions,
  actionFilter,
  onSetActionFilter,
  showCompletedActions,
  onToggleCompletedActions,
  onExploreAction,
}: MyActionsDrawerProps) {
  if (!render) return null

  const pendingItemsAll = actionItems.filter((i) => i.status === 'pending')
  const inProgressItems = actionItems.filter((i) => i.status === 'in_progress')
  const completedItems = actionItems.filter((i) => i.status === 'completed')

  const jiraPendingCount = pendingItemsAll.filter((i) => i.metadata?.source === 'jira').length
  const githubPendingCount = pendingItemsAll.filter((i) => i.metadata?.source === 'github').length
  const criticalPendingCount = pendingItemsAll.filter((i) => i.priority === 'critical').length

  const matchesFilter = (item: ActionItem): boolean => {
    if (actionFilter === 'all') return true
    if (actionFilter === 'jira') return item.metadata?.source === 'jira'
    if (actionFilter === 'github') return item.metadata?.source === 'github'
    if (actionFilter === 'critical') return item.priority === 'critical'
    return true
  }

  const filteredPending = pendingItemsAll.filter(matchesFilter)

  const pendingBySource = filteredPending.reduce<Record<string, ActionItem[]>>((acc, item) => {
    const key = String(item.metadata?.source || 'other')
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})

  const actionFilters: Array<{ key: typeof actionFilter; label: string; count: number }> = [
    { key: 'all', label: 'All', count: pendingItemsAll.length },
    { key: 'jira', label: 'Jira', count: jiraPendingCount },
    { key: 'github', label: 'GitHub', count: githubPendingCount },
    { key: 'critical', label: 'Critical', count: criticalPendingCount },
  ]

  const totalVisibleWork = filteredPending.length + inProgressItems.length

  const renderActionCard = (item: ActionItem) => {
    const mismatch = item.metadata?.connectionUser?.matches === false
    const cu = item.metadata?.connectionUser
    const who = cu?.email || cu?.displayName || cu?.providerUserId
    const srcKey = item.metadata?.source
    const srcLabel =
      srcKey === 'jira'
        ? 'Jira'
        : srcKey === 'github'
          ? 'GitHub'
          : item.metadata?.connection || 'integration'

    return (
      <div
        key={item.id}
        className="group relative overflow-hidden rounded-lg border border-subtle bg-foreground/[0.03] transition hover:bg-foreground/[0.05] hover:border-strong hover:shadow-sm"
      >
        <div
          className={clsx(
            'absolute left-0 top-0 bottom-0 w-1',
            priorityStripeClass(item.priority),
          )}
          aria-hidden="true"
        />
        <div className="py-3 pl-4 pr-3">
          {item.metadata?.url ? (
            <a
              href={item.metadata.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <h5 className="line-clamp-2 text-sm font-medium text-foreground group-hover:text-accent-soft-foreground group-hover:underline">
                {item.title}
              </h5>
            </a>
          ) : (
            <h5 className="line-clamp-2 text-sm font-medium text-foreground">
              {item.title}
            </h5>
          )}

          {item.description && (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
              {item.description}
            </p>
          )}

          {mismatch && who && (
            <div className="mt-2 flex items-center gap-1 rounded border border-warning/30 bg-warning-soft px-2 py-1 text-xs text-warning-foreground">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">Token belongs to {who}, not you</span>
            </div>
          )}

          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-subtle-foreground">
            <span className="font-medium text-muted-foreground">{srcLabel}</span>
            <span>&middot;</span>
            <span>{formatRelativeTime(item.updatedAt)}</span>
            {item.priority && item.priority !== 'none' && (
              <>
                <span>&middot;</span>
                <span className="capitalize">{item.priority}</span>
              </>
            )}
          </div>

          {item.tags && item.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {item.tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="rounded bg-foreground/[0.06] px-1.5 py-0.5 text-[10px] text-muted-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => onExploreAction(item)}
              className="rounded bg-accent px-3 py-1 text-xs font-medium text-white transition hover:bg-accent/90"
              title="Ask the assistant to look at this and suggest what to do"
            >
              Action
            </button>
            {item.metadata?.url && (
              <a
                href={item.metadata.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground transition hover:bg-foreground/[0.06] hover:text-foreground"
              >
                Open <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={clsx(
          'fixed inset-0 z-40 bg-black/30 transition-opacity duration-200',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside
        className={clsx(
          'fixed right-0 top-0 z-50 flex h-full w-full flex-col border-l border-subtle bg-card shadow-2xl transition-transform duration-200 ease-out sm:w-[30rem]',
          visible ? 'translate-x-0' : 'translate-x-full',
        )}
        role="dialog"
        aria-label="My Actions"
      >
        {/* Header */}
        <header className="flex items-start justify-between border-b border-subtle px-6 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-accent" />
              <h3 className="text-lg font-semibold text-foreground">My Actions</h3>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {actionItems.length === 0
                ? "You're not signed in to any integrations"
                : totalVisibleWork === 0
                  ? 'Nothing matches this filter'
                  : `${pendingItemsAll.length} pending${
                      inProgressItems.length > 0 ? ` \u00b7 ${inProgressItems.length} in progress` : ''
                    }`}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onRefreshActions}
              className="rounded-lg p-2 text-muted-foreground transition hover:bg-foreground/[0.06] hover:text-foreground"
              title="Refresh"
            >
              <RefreshCw className={clsx('h-4 w-4', loadingActions && 'animate-spin')} />
            </button>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-muted-foreground transition hover:bg-foreground/[0.06] hover:text-foreground"
              title="Close (Esc)"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* Filter chips */}
        {actionItems.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-x-auto border-b border-subtle px-6 py-3">
            {actionFilters.map((f) => {
              const active = actionFilter === f.key
              return (
                <button
                  key={f.key}
                  onClick={() => onSetActionFilter(f.key)}
                  className={clsx(
                    'flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition',
                    active
                      ? 'bg-accent text-white shadow-sm'
                      : 'bg-foreground/[0.06] text-muted-foreground hover:bg-foreground/[0.10]',
                  )}
                >
                  <span>{f.label}</span>
                  <span
                    className={clsx(
                      'rounded-full px-1.5 text-[10px] font-semibold',
                      active ? 'bg-white/20 text-white' : 'bg-foreground/[0.10] text-muted-foreground',
                    )}
                  >
                    {f.count}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loadingActions && actionItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-subtle-foreground">
              <RefreshCw className="mb-2 h-8 w-8 animate-spin" />
              <p className="text-sm">Loading actions...</p>
            </div>
          ) : actionItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="mb-3 rounded-full bg-success-soft p-3">
                <CheckCircle className="h-8 w-8 text-success" />
              </div>
              <p className="text-sm font-semibold text-foreground">You&apos;re all caught up</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Nothing needs your attention from your connected services right now.
              </p>
            </div>
          ) : totalVisibleWork === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="mb-3 rounded-full bg-foreground/[0.06] p-3">
                <ListTodo className="h-8 w-8 text-subtle-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                No {actionFilter === 'all' ? '' : actionFilter + ' '}items
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Try a different filter, or check back after the next sync.
              </p>
              {actionFilter !== 'all' && (
                <button
                  onClick={() => onSetActionFilter('all')}
                  className="mt-3 text-xs font-medium text-accent hover:text-accent-soft-foreground"
                >
                  Show all items &rarr;
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-6 px-6 py-4">
              {/* Pending grouped by source */}
              {sourceOrder
                .filter((src) => (pendingBySource[src] || []).length > 0)
                .map((src) => (
                  <section key={src}>
                    <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-subtle-foreground">
                      {sourceLabel[src]} &middot; {pendingBySource[src].length}
                    </h4>
                    <div className="space-y-2">
                      {pendingBySource[src].map(renderActionCard)}
                    </div>
                  </section>
                ))}

              {/* In progress */}
              {inProgressItems.length > 0 && (
                <section>
                  <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-subtle-foreground">
                    In progress &middot; {inProgressItems.length}
                  </h4>
                  <div className="space-y-2">
                    {inProgressItems.map((item) => (
                      <div
                        key={item.id}
                        className="rounded-lg border border-info/30 bg-info-soft p-3"
                      >
                        <div className="flex items-start gap-3">
                          <div className="rounded-lg bg-info-soft p-1.5">
                            <RefreshCw className="h-4 w-4 animate-spin text-info-foreground" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h5 className="truncate text-sm font-medium text-foreground">
                              {item.title}
                            </h5>
                            {item.description && (
                              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                {item.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Completed — collapsed by default */}
              {completedItems.length > 0 && (
                <section>
                  <button
                    onClick={onToggleCompletedActions}
                    className="flex w-full items-center justify-between rounded-md px-1 py-1 text-[11px] font-semibold uppercase tracking-wider text-subtle-foreground transition hover:text-muted-foreground"
                  >
                    <span>Completed today &middot; {completedItems.length}</span>
                    <ChevronDown
                      className={clsx(
                        'h-3.5 w-3.5 transition-transform',
                        showCompletedActions && 'rotate-180',
                      )}
                    />
                  </button>
                  {showCompletedActions && (
                    <div className="mt-2 space-y-2 opacity-70">
                      {completedItems.slice(0, 5).map((item) => (
                        <div
                          key={item.id}
                          className="rounded-lg border border-subtle bg-foreground/[0.04] p-3"
                        >
                          <div className="flex items-start gap-3">
                            <div className="rounded-lg bg-success-soft p-1.5">
                              <CheckCircle className="h-4 w-4 text-success-foreground" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h5 className="truncate text-sm text-foreground line-through">
                                {item.title}
                              </h5>
                              <p className="mt-0.5 text-xs text-subtle-foreground">
                                Completed {formatRelativeTime(item.updatedAt)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}
            </div>
          )}
        </div>

        {/* Footer with keyboard hint */}
        <footer className="flex items-center justify-between border-t border-subtle bg-foreground/[0.04] px-6 py-2.5 text-[11px] text-muted-foreground">
          <span>
            {totalVisibleWork} {totalVisibleWork === 1 ? 'item' : 'items'}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-subtle bg-card px-1.5 py-0.5 font-mono text-[10px]">
              Esc
            </kbd>
            <span>to close</span>
          </span>
        </footer>
      </aside>
    </>
  )
}
